import { useId, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { newReportsLabel } from '../pages/lib/executive.js';
import { Icon } from '../ui/Icon.js';
import { MenuItem, MenuPopover, useMenu } from '../ui/Menu.js';
import { Tooltip, type TooltipAnchorProps } from '../ui/Tooltip.js';
import { formatBadgeCount, formatList } from '../ui/format.js';
import { AccountMenu } from './AccountMenu.js';
import { AppMark } from './AppMark.js';
import { ExecutiveBadgeProvider, useExecutiveBadge } from './executive-badge.js';
import { useMe } from './me-context.js';
import { currentFolded, destinationsFor, foldDestinations, type Destination } from './navigation.js';
import { RegionErrorBoundary } from './RegionErrorBoundary.js';
import { RouteFocus } from './RouteFocus.js';
import { SkipLink } from './SkipLink.js';

type Surface = 'rail' | 'tab-bar';

/** What the bar draws for this person: the one fold over the destinations they are offered (navigation.ts). */
function useBar() {
  const me = useMe();
  return foldDestinations(destinationsFor(me));
}

/** A destination's count badge and its words: today only Executive carries one — reports newer than the reader's last open (ADR 0019). */
interface DestinationBadge {
  count: number;
  /** What the count means, said as the destination's description; null with no badge. */
  words: string | null;
}

const NO_BADGE: DestinationBadge = { count: 0, words: null };

function useBadgeOf(): (destination: Destination) => DestinationBadge {
  const executive = useExecutiveBadge();
  return (destination) =>
    destination.to === '/executive' && executive.count > 0 ? { count: executive.count, words: newReportsLabel(executive.count, executive.noneOpened) } : NO_BADGE;
}

/**
 * The count pinned to the icon's top-right (02-patterns §1.4: a pill, capped at 99+). Drawn aria-hidden: its words are
 * the destination's description (`BadgeWords`), so the name stays the destination's own and the tooltip carries nothing
 * beyond it.
 */
function NavBadge({ count }: { count: number }) {
  return <span className="nav-badge" aria-hidden="true"><span className="nav-badge__label">{formatBadgeCount(count)}</span></span>;
}

/** The badge's words, hidden and referenced by `aria-describedby` from the destination (a sibling, never in its name). */
function BadgeWords({ id, words }: { id: string; words: string }) {
  return <span id={id} hidden>{words}</span>;
}

function RailDestination({ destination, badge }: { destination: Destination; badge: DestinationBadge }) {
  const badgeId = useId();
  const shown = badge.count > 0 && badge.words !== null;
  return (
    <li className="rail__item">
      <Tooltip label={destination.label} labels placement="right">
        {(tooltip) => (
          <NavLink to={destination.to} end={destination.end} className="rail__link hit-area" {...tooltip} aria-describedby={shown ? badgeId : undefined}>
            <span className="nav-icon">
              <Icon name={destination.icon} size={20} />
              {shown ? <NavBadge count={badge.count} /> : null}
            </span>
          </NavLink>
        )}
      </Tooltip>
      {shown ? <BadgeWords id={badgeId} words={badge.words!} /> : null}
    </li>
  );
}

function TabDestination({ destination, badge }: { destination: Destination; badge: DestinationBadge }) {
  const badgeId = useId();
  const shown = badge.count > 0 && badge.words !== null;
  return (
    <li className="tab-bar__item">
      <NavLink to={destination.to} end={destination.end} className="tab-bar__link" aria-describedby={shown ? badgeId : undefined}>
        <span className="nav-icon">
          <Icon name={destination.icon} size={20} />
          {shown ? <NavBadge count={badge.count} /> : null}
        </span>
        <span className="tab-bar__label">{destination.label}</span>
      </NavLink>
      {shown ? <BadgeWords id={badgeId} words={badge.words!} /> : null}
    </li>
  );
}

/**
 * More (ADR 0019; design §2.13; 02-patterns §1.4): the destinations past the fifth, in order — on the tab bar a bottom
 * sheet, on the rail an anchored popover (MenuPopover presents a sheet below --bp-md and a popover above it, and each bar
 * exists on one side of that boundary only), each entry a MenuItem with its icon and label. More carries the active cue
 * — both signals — while the reader is on a folded destination, and the count badge when a folded destination carries
 * one. Its name is "More": the tooltip on the rail, the visible label on the tab bar.
 */
function MoreDestination({ surface, more, badgeOf }: { surface: Surface; more: readonly Destination[]; badgeOf: (destination: Destination) => DestinationBadge }) {
  const location = useLocation();
  const menu = useMenu();
  const badgeId = useId();
  const current = currentFolded(more, location.pathname);
  // More's count is its folded destinations' together, and its words are theirs, each said whole (05-content §8).
  const folded = more.map(badgeOf).filter((entry) => entry.count > 0 && entry.words !== null);
  const badge = folded.reduce((sum, entry) => sum + entry.count, 0);
  const badgeWords = folded.length > 0 ? formatList(folded.map((entry) => entry.words!)) : null;
  const classes = [surface === 'rail' ? 'rail__link nav-more hit-area' : 'tab-bar__link nav-more', current ? 'is-current' : ''].filter(Boolean).join(' ');
  const trigger = (tooltip: TooltipAnchorProps | null) => (
    <button
      type="button"
      className={classes}
      {...tooltip}
      {...menu.triggerProps}
      // Why both key handlers: the tooltip closes on Escape, the menu trigger opens on the arrows, Enter and Space. And why
      // Escape closes an open menu here too: the menu's first item takes focus a frame after it opens, and an Escape in
      // that frame lands on the trigger — it still closes the menu (03-components §5.1), the focus staying where it is.
      onKeyDown={(event: ReactKeyboardEvent<HTMLElement>) => {
        tooltip?.onKeyDown(event);
        if (event.key === 'Escape' && menu.open && !event.nativeEvent.isComposing) {
          event.preventDefault();
          menu.close();
          return;
        }
        menu.triggerProps.onKeyDown(event);
      }}
      // Why `true` and not `page`: More is not the page; it is the item of the bar that holds the page (WAI-ARIA's
      // "current item within a set"), the same two visual signals as an active destination beside it.
      aria-current={current ? 'true' : undefined}
      aria-describedby={badge > 0 ? badgeId : undefined}
    >
      <span className="nav-icon">
        <Icon name="more-horizontal" size={20} />
        {badge > 0 ? <NavBadge count={badge} /> : null}
      </span>
      {surface === 'tab-bar' ? <span className="tab-bar__label">More</span> : null}
    </button>
  );
  return (
    <li className={surface === 'rail' ? 'rail__item' : 'tab-bar__item'}>
      {surface === 'rail' ? <Tooltip label="More" labels placement="right">{(tooltip) => trigger(tooltip)}</Tooltip> : trigger(null)}
      {badge > 0 && badgeWords !== null ? <BadgeWords id={badgeId} words={badgeWords} /> : null}
      <MenuPopover menu={menu} label="More destinations" align="start" minWidth="12rem">
        <div role="menu" aria-label="More" className="menu">
          {more.map((destination) => (
            <MenuItem
              key={destination.to}
              to={destination.to}
              icon={destination.icon}
              current={current?.to === destination.to}
              description={badgeOf(destination).words ?? undefined}
            >
              {destination.label}
            </MenuItem>
          ))}
        </div>
      </MenuPopover>
    </li>
  );
}

/** The desktop icon rail (02-patterns §1.4): icon-only, name in the accessible name and a tooltip on hover and focus. */
function NavRail() {
  const { shown, more } = useBar();
  const badgeOf = useBadgeOf();
  return (
    <nav className="rail" aria-label="Primary">
      <ul className="rail__list" role="list">
        {shown.map((destination) => <RailDestination key={destination.to} destination={destination} badge={badgeOf(destination)} />)}
        {more.length > 0 ? <MoreDestination surface="rail" more={more} badgeOf={badgeOf} /> : null}
      </ul>
    </nav>
  );
}

/** The mobile bottom tab bar (02-patterns §1.4): the same destinations, icon plus visible label, and More past six. */
function TabBar() {
  const { shown, more } = useBar();
  const badgeOf = useBadgeOf();
  return (
    <nav className="tab-bar" aria-label="Primary">
      <ul className="tab-bar__list" role="list">
        {shown.map((destination) => <TabDestination key={destination.to} destination={destination} badge={badgeOf(destination)} />)}
        {more.length > 0 ? <MoreDestination surface="tab-bar" more={more} badgeOf={badgeOf} /> : null}
      </ul>
    </nav>
  );
}

/**
 * The authenticated shell (02-patterns §1): top bar spanning the width, icon rail at ≥ --bp-md,
 * bottom tab bar below it, content filling the rest and scrolling with the page. Built once;
 * every page renders inside it through the router outlet.
 */
export function AppShell() {
  const location = useLocation();
  return (
    <ExecutiveBadgeProvider>
      <div className="app-shell">
        <SkipLink />
        <div className="shell-top">
          <header className="top-bar">
            <AppMark className="top-bar__mark" />
            <div className="top-bar__space" />
            <div className="top-bar__account">
              <AccountMenu />
            </div>
          </header>
        </div>
        <NavRail />
        <main id="main" className="shell-main" tabIndex={-1}>
          <RouteFocus />
          <RegionErrorBoundary region="this page" resetKey={location.pathname}>
            <Outlet />
          </RegionErrorBoundary>
        </main>
        <TabBar />
      </div>
    </ExecutiveBadgeProvider>
  );
}
